import { useMemo } from 'react'

import { useModals, useNumericParams, useSetState } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import CreateOrEditTaskListTemplateItemModal from '@modals/CreateOrEditTaskListTemplateItemModal'
import CreateOrEditTaskListTemplateModal from '@modals/CreateOrEditTaskListTemplateModal'
import CreateOrEditTrackingLinkModal from '@modals/CreateOrEditTrackingLinkModal'
import UploadEntityAssetsModal from '@modals/UploadEntityAssetsModal'

import PageContent from './components/PageContent'

export type PageContextPayload = {
  callbacks: {
    deleteAttachment: (payload: HandleCallbackActionParams) => void,
    deleteTrackingLink: (payload: HandleCallbackActionParams) => void,
    showCreateOrEditTaskListTemplateItemModal: (payload: object) => void,
    showCreateOrEditTrackingLinkModal: (payload: object) => void,
    showUploadEntityAssetsModal: (payload: object) => void,
    updateTaskListTemplate: (payload: HandleCallbackActionParams) => void,
  },
}

const callbacks = (component: string, setState: (state: Partial<typeof defaultState>) => void) => {
  const componentCallbacks = {
    CreateOrEditTaskListTemplateItemModal: {
      closeModal: () => setState({ showCreateOrEditTaskListTemplateItemModal: false }),
      createTaskListTemplateItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateTaskListTemplateItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditTaskListTemplateModal: {
      closeModal: () => setState({ showCreateOrEditTaskListTemplateModal: false }),
      updateTaskListTemplate: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditTrackingLinkModal: {
      closeModal: () => setState({ showCreateOrEditTrackingLinkModal: false }),
      createTrackingLink: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateTrackingLink: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    UploadEntityAssetsModal: {
      closeModal: () => setState({ showUploadEntityAssetsModal: false }),
      deleteAttachment: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  // @ts-expect-error TS7053
  return componentCallbacks[component] || {}
}

const defaultState = {
  showCreateOrEditTaskListTemplateItemModal: false,
  showCreateOrEditTaskListTemplateModal: false,
  showCreateOrEditTrackingLinkModal: false,
  showUploadEntityAssetsModal: false,
}

const Details = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showCreateOrEditTaskListTemplateItemModal,
    showCreateOrEditTaskListTemplateModal,
    showCreateOrEditTrackingLinkModal,
    showUploadEntityAssetsModal,
  } = state

  const { taskListTemplateId } = useNumericParams()

  const modalContext = useModals()
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      deleteAttachment: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteTrackingLink: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      showCreateOrEditTaskListTemplateItemModal: (payload: object) => {
        setModalData('CreateOrEditTaskListTemplateItemModal', payload)
        setState({ showCreateOrEditTaskListTemplateItemModal: true })
      },
      showCreateOrEditTrackingLinkModal: (payload: object) => {
        setModalData('CreateOrEditTrackingLinkModal', payload)
        setState({ showCreateOrEditTrackingLinkModal: true })
      },
      showUploadEntityAssetsModal: (payload: object) => {
        setModalData('UploadEntityAssetsModal', payload)
        setState({ showUploadEntityAssetsModal: true })
      },
      updateTaskListTemplate: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent taskListTemplateId={taskListTemplateId} />

        <CreateOrEditTaskListTemplateModal
          callbacks={callbacks('CreateOrEditTaskListTemplateModal', setState)}
          showModal={showCreateOrEditTaskListTemplateModal}
        />

        <CreateOrEditTaskListTemplateItemModal
          callbacks={callbacks('CreateOrEditTaskListTemplateItemModal', setState)}
          showModal={showCreateOrEditTaskListTemplateItemModal}
        />

        <CreateOrEditTrackingLinkModal
          callbacks={callbacks('CreateOrEditTrackingLinkModal', setState)}
          showModal={showCreateOrEditTrackingLinkModal}
        />

        <UploadEntityAssetsModal
          callbacks={callbacks('UploadEntityAssetsModal', setState)}
          showModal={showUploadEntityAssetsModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Details
